'use client';
import { Box, Checkbox, Typography } from '@mui/material';
import { useAnalyticsSkuViewedContext } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { ElementViewed, useElementViewedContext } from '@repo/segment/Analytics/ElementViewed';
import type { ProductCard } from '@repo/supergraph/roundup/base/types';
import { forwardRef } from 'react';
import { useCompareOffersStore } from '../CompareOffersStore';
import {
  useCanSelectOfferForCompare,
  useIsOfferSelectedForCompare,
} from '../CompareOffersStore/hooks';

type Props = {
  offer: ProductCard;
  index?: number;
};

export const AddToCompareCheckbox = ({ offer, index }: Props) => {
  const offerId = offer.marketplaceEntity.id;
  const { skuImpressionId } = useAnalyticsSkuViewedContext();

  return (
    <ElementViewed
      entityName="compare_card"
      sectionName="banking_cd_product_card_compare"
      itemPosition={index}
      additionalAttributes={{
        sku_id: offerId,
        sku_impression_id: skuImpressionId,
        page_type: 'roundup',
      }}
    >
      <AddToCompareCheckboxContent offer={offer} index={index} skuImpressionId={skuImpressionId} />
    </ElementViewed>
  );
};

type ContentProps = {
  offer: ProductCard;
  index?: number;
  skuImpressionId?: string;
};

const AddToCompareCheckboxContent = forwardRef<HTMLLabelElement, ContentProps>(
  ({ offer, index, skuImpressionId }, ref) => {
    const offerId = offer.marketplaceEntity.id;
    const isSelected = useIsOfferSelectedForCompare(offerId);
    const isDisabled = !useCanSelectOfferForCompare(offerId);
    const selectedOffersCount = useCompareOffersStore(state => state.selectedOffers.length);
    const toggleOfferSelection = useCompareOffersStore(state => state.toggleOfferSelection);
    const { fireElementInteracted } = useElementViewedContext();

    const handleChange = () => {
      if (isDisabled) return;
      const selectionCountAfterToggle = isSelected
        ? selectedOffersCount - 1
        : selectedOffersCount + 1;
      toggleOfferSelection(offer, skuImpressionId);
      fireElementInteracted(isSelected ? 'unselect' : 'select', {
        itemPosition: index,
        additionalAttributes: {
          sku_id: offerId,
          sku_impression_id: skuImpressionId,
          selection_count: selectionCountAfterToggle,
          page_type: 'roundup',
        },
      });
    };

    return (
      <Box
        ref={ref}
        component="label"
        data-testid={`add-to-compare-${offerId}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          px: 2,
          py: 1,
          borderRadius: { xs: 0.5, md: 2 },
          backgroundColor: 'neutral.lightest',
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <Checkbox
          checked={isSelected}
          onChange={handleChange}
          disabled={isDisabled}
          disableRipple
          sx={{
            padding: 0,
            color: isDisabled ? 'neutral.light' : 'neutral.darker',
            '&.Mui-checked': { color: 'primary.main' },
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        />
        <Typography
          variant="textSmall"
          sx={{
            fontSize: { xs: 12, md: 14 },
          }}
        >
          {isSelected ? 'Added to compare' : 'Add to compare'}
        </Typography>
      </Box>
    );
  },
);
